import { useEffect, useMemo, useRef } from 'react'
import { get, sortBy } from 'lodash'
import { useLazyGetAccountDetailsQuery } from 'api/accounts/getAccountDetails'
import { useLazyFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { selectServiceActions } from 'data/meta/metaSelectors'
import { useGetAllPricedServicesQuery } from 'api/pricing/pricingServiceCrud'
import { Z_INDEX } from 'theme/zIndex'

import { combineAddressInfo } from 'utils/helper'
import { RECURRENCE_V2 } from 'settings/constants/service'

/**
 * Initializes CreateDraftServiceForm data sources and default behaviors.
 * - Fetches account details and meta filters
 * - Derives locations, actionList, and active pricing services
 * - Initializes/auto-selects location field on open
 * - Manages default onRequestAction based on selected priced service
 */
export function useCreateDraftServiceFormInit({
  isOpen = false,
  getValues,
  setValue,
  accountId,
  selectedLocationId,
  selectedPricedService,
}) {
  const isSelectedPricedServiceOnRequest = selectedPricedService?.serviceType === RECURRENCE_V2.ON_REQUEST

  // Account details
  const [getAccountDetails, { data: accountDetailsData }] = useLazyGetAccountDetailsQuery()

  useEffect(() => {
    if (accountId) {
      getAccountDetails({ accountId })
    }
  }, [accountId])

  const locations = useMemo(
    () =>
      sortBy(
        get(accountDetailsData, 'locations', [])
          .filter(({ deactivated }) => !deactivated)
          .map(location => {
            const addressName = get(location, 'address.addressName')
            const completeAddress = combineAddressInfo(get(location, 'address', {}))
            const label = !addressName ? completeAddress : `${addressName}: ${completeAddress}`
            return { ...location, label, value: location.id }
          }),
        'label'
      ),
    [accountDetailsData]
  )

  // Meta filters and derived action list
  const [fetchMetaFilters, { data: metaFiltersData }] = useLazyFetchMetaFiltersQuery()
  useEffect(() => {
    fetchMetaFilters({})
  }, [])
  const actionList = useMemo(() => selectServiceActions(metaFiltersData), [metaFiltersData])

  // Active priced services
  const { data } = useGetAllPricedServicesQuery()
  const pricingServices = useMemo(() => (Array.isArray(data) ? data.filter(({ active = false }) => active === true) : []), [data])

  // Slot props for MUI Autocomplete/Date pickers
  const slotPropsPopper = useMemo(
    () => ({
      popper: {
        sx: { zIndex: Z_INDEX.MENU },
      },
    }),
    []
  )

  // When dialog is opened with no location selected, default to the first active location (run once per open)
  const hasAutoSelectedLocationOnOpen = useRef(false)
  useEffect(() => {
    // Reset guard when dialog closes
    if (!isOpen) {
      hasAutoSelectedLocationOnOpen.current = false
      return
    }
    if (hasAutoSelectedLocationOnOpen.current) return

    const formLocation = getValues?.().location
    const hasSelectedLocation = !!selectedLocationId || !!formLocation

    if (!hasSelectedLocation && Array.isArray(locations) && locations.length > 0) {
      const first = locations[0]
      setValue?.('location', first, { shouldValidate: true })
      const firstId = first?.id ?? first?.value ?? ''
      if (firstId) {
        //
      }
      hasAutoSelectedLocationOnOpen.current = true
    } else if (hasSelectedLocation) {
      hasAutoSelectedLocationOnOpen.current = true
    }
  }, [isOpen, locations, selectedLocationId, getValues, setValue])

  // Initialize default selected location from Redux when dialog resets/opens
  useEffect(() => {
    if (!selectedLocationId) return
    const current = getValues?.().location
    if (current) return
    const match = locations.find(l => l?.id === selectedLocationId || l?.value === selectedLocationId)
    if (match) {
      setValue?.('location', match, { shouldValidate: true })
    }
  }, [selectedLocationId, locations, getValues, setValue])

  // Manage default onRequestAction selection when On-Request service is chosen
  useEffect(() => {
    if (!isSelectedPricedServiceOnRequest) {
      setValue?.('onRequestAction', null)
      return
    }
    const defaultActionType = selectedPricedService?.actionType
    if (defaultActionType && Array.isArray(actionList)) {
      const match = actionList.find(a => a?.actionType === defaultActionType)
      setValue?.('onRequestAction', match || null)
    }
  }, [isSelectedPricedServiceOnRequest, selectedPricedService, actionList, setValue])

  return { locations, actionList, pricingServices, slotPropsPopper }
}
